/**
 * TV scoreboard — /scoreboard/:pin. Public, no login.
 *
 * An always-on standings board for a second TV in the bar. It stays up all
 * night. It shows standings through the last completed round, the same
 * rule as the projector standings slide. While a round is in progress, the
 * board keeps the previous completed round.
 *
 * Data: GET /api/events/by-pin/:pin/scoreboard returns a whitelist payload
 * (rank, team name, total, round info). It never carries answers.
 *
 * Live updates: the page joins the socket.io room for its pin with
 * 'join-scoreboard'. The server emits 'scoreboard-update' when a round
 * completes, when a score in a completed round changes, when a team or a
 * setting changes, and on every (re)join. While the socket is down, the
 * page polls the REST endpoint.
 *
 * Visual language follows PresentationView (dark stage, gold Playfair
 * heading, gold/silver/bronze rows), not the Radix admin styling.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { io, Socket } from 'socket.io-client';
import api from '../../utils/api';
import config from '../../config';
import {
  scoreboardLayout,
  pageSlice,
  slotFor,
  rankMoves,
  ordinal,
  type RankMove,
} from '../../utils/scoreboardLayout';
import './ScoreboardView.css';

interface ScoreboardEntry {
  teamId: string;
  teamName: string;
  rank: number;
  total: number;
}

interface ScoreboardOn {
  enabled: true;
  pin: string;
  title: string | null;
  venueName: string | null;
  status: 'LIVE' | 'COMPLETE' | 'DRAFT';
  totalRounds: number;
  nowPlayingRound: number | null;
  // Last completed round on the board. null = every round (night COMPLETE).
  // 0 = no round is complete yet.
  throughRound: number | null;
  topN: number | null;
  teamCount: number;
  standings: ScoreboardEntry[];
  updatedAt: string;
}

interface ScoreboardOff {
  enabled: false;
  pin: string;
}

type Scoreboard = ScoreboardOn | ScoreboardOff;

// Poll this often while the socket is down.
const POLL_MS = 15_000;
// Time on each page when more than 24 teams rotate.
const PAGE_MS = 12_000;
// How long an up or down arrow stays on a row after a rank change.
const MOVE_FLASH_MS = 6_000;

function socketOrigin(): string {
  // Socket.io reads a URL path as a namespace. The server listens on the
  // default namespace, so connect to the origin only.
  try {
    return new URL(config.apiBaseUrl).origin;
  } catch {
    return config.apiBaseUrl;
  }
}

function rankClass(rank: number): string {
  if (rank === 1) return 'rank-gold';
  if (rank === 2) return 'rank-silver';
  if (rank === 3) return 'rank-bronze';
  return '';
}

export default function ScoreboardView() {
  const { pin } = useParams<{ pin: string }>();
  const [board, setBoard] = useState<Scoreboard | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [connected, setConnected] = useState(false);
  const [page, setPage] = useState(0);
  const [moves, setMoves] = useState<Record<string, RankMove>>({});
  const prevStandingsRef = useRef<ScoreboardEntry[]>([]);
  const moveTimerRef = useRef<number | undefined>(undefined);

  const applyBoard = useCallback((next: Scoreboard) => {
    if (next.enabled) {
      const found = rankMoves(prevStandingsRef.current, next.standings);
      if (Object.keys(found).length > 0) {
        setMoves(found);
        window.clearTimeout(moveTimerRef.current);
        moveTimerRef.current = window.setTimeout(() => setMoves({}), MOVE_FLASH_MS);
      }
      prevStandingsRef.current = next.standings;
    } else {
      prevStandingsRef.current = [];
    }
    setBoard(next);
  }, []);

  const fetchBoard = useCallback(async () => {
    if (!pin) return;
    try {
      const res = await api.get(`/api/events/by-pin/${pin}/scoreboard`);
      setNotFound(false);
      applyBoard(res.data.data.scoreboard as Scoreboard);
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 404) setNotFound(true);
    }
  }, [pin, applyBoard]);

  // First load over REST, so the board shows even if the socket is slow.
  useEffect(() => {
    fetchBoard();
  }, [fetchBoard]);

  // Live updates over socket.io.
  useEffect(() => {
    if (!pin) return;
    const socket: Socket = io(socketOrigin(), { transports: ['websocket', 'polling'] });
    socket.on('connect', () => {
      setConnected(true);
      // Join on every connect and reconnect. The server replies with the
      // current board, so a reconnect catches up at once.
      socket.emit('join-scoreboard', { pin });
    });
    socket.on('disconnect', () => setConnected(false));
    socket.on('connect_error', () => setConnected(false));
    socket.on('scoreboard-update', (payload: Scoreboard) => {
      if (payload && payload.pin === pin) applyBoard(payload);
    });
    return () => {
      socket.disconnect();
    };
  }, [pin, applyBoard]);

  // Fallback: poll while the socket is down.
  useEffect(() => {
    if (connected) return;
    const t = window.setInterval(fetchBoard, POLL_MS);
    return () => window.clearInterval(t);
  }, [connected, fetchBoard]);

  useEffect(() => () => window.clearTimeout(moveTimerRef.current), []);

  const standings = board?.enabled ? board.standings : [];
  const layout = useMemo(() => scoreboardLayout(standings.length), [standings.length]);

  // Rotate pages when more teams exist than fit on one screen.
  useEffect(() => {
    if (layout.pageCount <= 1) {
      setPage(0);
      return;
    }
    const t = window.setInterval(() => setPage((p) => (p + 1) % layout.pageCount), PAGE_MS);
    return () => window.clearInterval(t);
  }, [layout.pageCount]);

  const connDot = (
    <span
      className={`sb-connection ${connected ? 'connected' : 'disconnected'}`}
      title={connected ? 'Live' : 'Reconnecting. The board refreshes every 15 seconds.'}
    />
  );

  if (notFound) {
    return (
      <div className="scoreboard-view">
        <div className="sb-message">
          <h1 className="sb-message-title">Game Night not found</h1>
          <p className="sb-message-body">Check the PIN in the scoreboard address.</p>
        </div>
      </div>
    );
  }

  if (!board) {
    return (
      <div className="scoreboard-view">
        {connDot}
        <div className="sb-message">
          <p className="sb-message-body">Loading the scoreboard…</p>
        </div>
      </div>
    );
  }

  if (!board.enabled) {
    return (
      <div className="scoreboard-view">
        {connDot}
        <div className="sb-message">
          <h1 className="sb-message-title">Scoreboard</h1>
          <p className="sb-message-body">
            The scoreboard is off. It appears here when the host turns it on.
          </p>
        </div>
      </div>
    );
  }

  const visible = pageSlice(standings, page, layout.pageSize);
  const pageNum = (page % layout.pageCount) + 1;
  const roundNow = board.nowPlayingRound ?? board.throughRound ?? 0;
  const isFinal = board.status === 'COMPLETE';

  return (
    <div className="scoreboard-view">
      {connDot}

      <header className="sb-header">
        <div className="sb-header-left">
          <h1 className="sb-heading">{isFinal ? 'Final Standings' : 'Standings'}</h1>
          {(board.title || board.venueName) && (
            <div className="sb-subtitle">
              {board.title}
              {board.title && board.venueName ? ' · ' : ''}
              {board.venueName}
            </div>
          )}
        </div>
        <div className="sb-header-right">
          {!isFinal && board.totalRounds > 0 && roundNow > 0 && (
            <div className="sb-round">
              Round <b>{roundNow}</b> of {board.totalRounds}
            </div>
          )}
          <div className="sb-meta">
            {board.throughRound != null && board.throughRound > 0 && (
              <span>After Round {board.throughRound}</span>
            )}
            {board.topN != null && <span className="sb-chip">Top {board.topN}</span>}
            {layout.pageCount > 1 && (
              <span className="sb-chip">
                Page {pageNum} of {layout.pageCount}
              </span>
            )}
          </div>
        </div>
      </header>

      {standings.length === 0 ? (
        <div className="sb-message">
          <p className="sb-message-body">
            {board.throughRound === 0
              ? 'Standings appear here after Round 1.'
              : 'No scores yet.'}
          </p>
        </div>
      ) : (
        <div
          className="sb-board"
          data-cols={layout.columns}
          style={
            {
              '--cols': layout.columns,
              '--rows': layout.rowsPerColumn,
            } as React.CSSProperties
          }
        >
          {visible.map((s, i) => {
            const { col, row } = slotFor(i, layout.rowsPerColumn);
            const move = moves[s.teamId];
            return (
              <div
                key={s.teamId}
                className={`sb-row ${rankClass(s.rank)} ${move ? `moved-${move}` : ''}`}
                style={{ '--col': col, '--row': row } as React.CSSProperties}
              >
                <span className="sb-rank">{ordinal(s.rank)}</span>
                <span className="sb-team">{s.teamName}</span>
                {move && (
                  <span className={`sb-move ${move}`} aria-label={move === 'up' ? 'Moved up' : 'Moved down'}>
                    {move === 'up' ? '▲' : '▼'}
                  </span>
                )}
                {/* The key restarts the bump animation when the total changes. */}
                <span className="sb-total" key={s.total}>
                  {s.total}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
